import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash, FaApple } from "react-icons/fa";
import ChangePasswordFirstTimeModal from "../Modals/changePasswordFirstTimeModal/ChangePasswordFirstTimeModal.jsx";
import SetPasswordReminderModal from "../Modals/setPasswordReminderModal/SetPasswordReminderModal";
import PasswordUpdatedModal from "../Modals/passwordUpdatedModal/PasswordUpdatedModal.jsx";
import AccessDeniedModal from "../Modals/accessdeniedmodal/Accessdeniedmodal.jsx";
import "./LoginScreen.css";
import axios from "axios";
import { useAuth } from "../context/auth";

const LoginScreen = () => {
  const navigate = useNavigate();
  const authenticate = useAuth();
  const location = useLocation();
  const hasNavigatedRef = useRef(false);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRole, setSelectedRole] = useState(() => {
    const roleFromState = location.state?.role;
    const roleFromStorage = localStorage.getItem("pendingLoginRole");
    const roleFromSession = sessionStorage.getItem("currentLoginRole");
    const params = new URLSearchParams(location.search);
    const signUpRole = params.get("role");

    const finalRole =
      roleFromState || roleFromStorage || roleFromSession || signUpRole || "";

    if (finalRole) {
      localStorage.setItem("pendingLoginRole", finalRole);
      sessionStorage.setItem("currentLoginRole", finalRole);
    }

    return finalRole;
  });

  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [setNewPasswordModalVisible, setSetNewPasswordModalVisible] =
    useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [tempAuthData, setTempAuthData] = useState(null);

  const [showSetPasswordReminderModal, setShowSetPasswordReminderModal] =
    useState(false);
  const [showPasswordUpdatedModal, setShowPasswordUpdatedModal] =
    useState(false);

  const [accessDenied, setAccessDenied] = useState({
    visible: false,
    deniedRole: "",
    availableRoles: [],
  });

  const { inviteCode, hostId } = location.state || {};
  const queryParams = new URLSearchParams(window.location.search);
  const queryInviteCode = queryParams.get("inviteCode" || "invitecode");
  const queryHostId = queryParams.get("hostId");
  const logoImage2 = "/images/logoOnBlack.png";

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (selectedRole) {
      localStorage.setItem("pendingLoginRole", selectedRole);
      sessionStorage.setItem("currentLoginRole", selectedRole);
    }
  }, [selectedRole]);

  useEffect(() => {
    const newRole = location.state?.role;
    if (newRole && newRole !== selectedRole) {
      setSelectedRole(newRole);
    }
  }, [location.state?.role]);

  useEffect(() => {
    if (location.state?.prefillEmail === "true" && location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  useEffect(() => {
    if (!isAuthenticated || hasNavigatedRef.current) return;

    hasNavigatedRef.current = true;

    const fromPath = location.state?.from?.pathname;

    if (fromPath && fromPath !== "/" && !fromPath.startsWith("/login")) {
      localStorage.removeItem("pendingLoginRole");
      sessionStorage.removeItem("currentLoginRole");
      navigate(fromPath, { replace: true, state: true });
      return;
    }

    const roleToUse =
      selectedRole ||
      localStorage.getItem("pendingLoginRole") ||
      sessionStorage.getItem("currentLoginRole");

    localStorage.removeItem("pendingLoginRole");
    sessionStorage.removeItem("currentLoginRole");

    if (location.state?.fromInviteLink) {
      navigate("/invites?reload=true", { replace: true });
    } else if (roleToUse) {
      navigate(`/home/${roleToUse}`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasNavigatedRef.current = false;
    }
  }, [isAuthenticated]);

  const validateFields = () => {
    let valid = true;
    let newErrors = { email: "", password: "" };

    if (!email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email.";
      valid = false;
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleForgotPasswordClick = () => {
    navigate("/password-recovery");
  };

  const handleOtpVerify = (code) => {
    setOtpModalVisible(false);
    setSetNewPasswordModalVisible(true);
  };

  const handlePasswordChangeSuccess = async (newToken, updatedUser) => {
    try {
      const roleToPreserve = tempAuthData?.role || selectedRole;

      setShowChangePasswordModal(false);
      setTempAuthData(null);
      setIsLoading(false);

      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userData");
      if (roleToPreserve) {
        localStorage.setItem("pendingLoginRole", roleToPreserve);
        sessionStorage.setItem("currentLoginRole", roleToPreserve);
      }

      setShowPasswordUpdatedModal(true);
    } catch (error) {
      alert("Password updated but navigation failed. Please login manually.");
    }
  };

  const showAccessDeniedModal = (deniedRole, availableRoles) => {
    setAccessDenied({ visible: true, deniedRole, availableRoles });
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setSuccessModalVisible(false);

    if (validateFields()) {
      try {
        setIsLoading(true);

        const response = await axios.post("/user/login", {
          email,
          password,
          role: selectedRole,
        });

        const { successStatus, data, requiresPasswordChange } = response.data;

        if (requiresPasswordChange) {
          const {
            token,
            userId,
            email: userEmail,
            firstName,
            lastName,
            user,
          } = data;

          setTempAuthData({
            token,
            userId,
            email: userEmail || email,
            firstName: firstName || "",
            lastName: lastName || "",
            user,
            role: selectedRole,
          });

          if (selectedRole) {
            localStorage.setItem("pendingLoginRole", selectedRole);
            sessionStorage.setItem("currentLoginRole", selectedRole);
          }

          setShowSetPasswordReminderModal(true);
          setIsLoading(false);
          return;
        }

        const { user, token, refreshToken } = data;

        if (successStatus === true) {
          const fromEventId = location.state?.from?.pathname?.match(
            /\/eventManagementDetails\/([^/]+)/
          )?.[1];

          if (fromEventId && selectedRole === "guest") {
            try {
              const guestCheckResponse = await axios.get(
                `/event/${fromEventId}/guests`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              const guestEmails = (guestCheckResponse.data?.guests || []).map(
                (g) => (g.email || g.guestEmail || "").toLowerCase()
              );

              if (
                guestEmails.length > 0 &&
                !guestEmails.includes(email.trim().toLowerCase())
              ) {
                const userRoles = user?.roles || user?.availableRoles || [];
                showAccessDeniedModal("guest", userRoles);
                setIsLoading(false);
                return;
              }
            } catch {
              // guest check failed silently — proceed with login
            }
          }

          const userWithActiveRole = {
            ...user,
            activeRole: selectedRole,
          };

          authenticate.authenticate(token, userWithActiveRole, refreshToken);

          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("userEmail", email);

          const navigationState = {
            selectedRole,
            inviteCode: inviteCode,
            hostId: hostId || queryHostId || null,
            reload: true,
          };

          const fromPath = location.state?.from?.pathname;

          if (fromPath && fromPath !== "/" && !fromPath.startsWith("/login")) {
            navigate(fromPath, { replace: true, state: true });
            return;
          }
          const inviteFromState = location.state?.fromInviteLink;
          const inviteCodeFromState =
            location.state?.inviteCode || queryInviteCode;
          const hostIdFromState = location.state?.hostId;

          if (inviteFromState && inviteCodeFromState && hostIdFromState) {
            navigate("/invites?reload=true", {
              replace: true,
              state: {
                inviteCode: inviteCodeFromState,
                hostId: hostIdFromState,
              },
            });
            return;
          }

          if (selectedRole) {
            navigate(`/home/${selectedRole}`, {
              state: navigationState,
              replace: true,
            });
          } else {
            navigate("/", {
              state: navigationState,
              replace: true,
            });
          }

          if (selectedRole) {
            localStorage.setItem("pendingLoginRole", selectedRole);
            sessionStorage.setItem("currentLoginRole", selectedRole);
          }
        }
      } catch (error) {
        localStorage.setItem("userEmail", email);

        const status = error.response?.status;

        switch (status) {
          case 403: {
            const availableRoles = error.response?.data?.data?.user.roles || [];
            showAccessDeniedModal(selectedRole, availableRoles);
            break;
          }
          case 409:
            navigate(
              `/signup?email=${email}&source=LOGIN&inviteCode=${
                inviteCode || queryInviteCode || ""
              }&hostId=${hostId || queryHostId || ""}`
            );
            break;
          case 400:
            alert(
              "No account found with this email. Please create a new account to register."
            );
            break;
          case 401:
            alert("Invalid email or password.");
            break;
          case 423:
            alert(
              "Your account is suspended. Please check your email or contact support."
            );
            break;
          case 410:
            alert(
              "Your account has been deleted. Please contact support if this was a mistake."
            );
            break;
          default:
            alert("Authentication failed. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePasswordUpdatedContinue = () => {
    setShowPasswordUpdatedModal(false);

    const roleToUse =
      tempAuthData?.role ||
      selectedRole ||
      localStorage.getItem("pendingLoginRole") ||
      sessionStorage.getItem("currentLoginRole");

    if (roleToUse) {
      localStorage.setItem("pendingLoginRole", roleToUse);
      sessionStorage.setItem("currentLoginRole", roleToUse);
    }

    navigate("/login", {
      state: {
        email: tempAuthData?.email || email,
        prefillEmail: "true",
        role: roleToUse,
      },
      replace: true,
    });
  };

  const handleChangePasswordSubmit = async (passwordData) => {
    try {
      setIsLoading(true);

      const response = await axios.post(
        "/user/change-password-first-time",
        {
          userId: tempAuthData.userId,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${tempAuthData.token}`,
          },
        }
      );

      if (response.data.success) {
        const { token: newToken, user: updatedUser } = response.data.data;
        await handlePasswordChangeSuccess(newToken, updatedUser);
      }
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Failed to change password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const OtpModal = ({
    visible,
    email,
    onClose,
    onVerify,
    onResend,
    onEditEmail,
  }) => {
    if (!visible) return null;
    return <div className="modal">OTP Modal</div>;
  };

  return (
    <div className="login-container">
      <div className="login-img-container">
        <img
          src="/images/onboarding-page-image.webp"
          alt="NimbleBash celebration"
          className="login-celebration-img"
        />
      </div>

      <div className="login-content">
        <div className="login-header">
          <div className="login-logo-container">
            <h1 className="login-brand-name">
              <img src={logoImage2} alt="Logo" className="login-logo" />
            </h1>
          </div>

          <div className="">
            <h2 className="login-title">Login</h2>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  placeholder="linda@framcreative.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-wrapper">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    id="password"
                    placeholder="Enter Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={errors.password ? "input-error" : ""}
                  />
                  <button
                    type="button"
                    className="toggle-password-button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    {passwordVisible ? (
                      <FaEye className="eye-icon" />
                    ) : (
                      <FaEyeSlash className="eye-icon" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="error-text">{errors.password}</p>
                )}
              </div>

              <div className="forgot-password-container">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={handleForgotPasswordClick}
                >
                  Forgot Password?
                </button>
              </div>
            </form>

            {/* <div className="social-login-container">
              <p className="continue-with-text">Or Continue with</p>

              <div className="social-buttons">
                <button className="social-button google-button">
                  <svg
                    className="google-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Google</span>
                </button>

                <button className="social-button apple-button">
                  <FaApple className="apple-icon" />
                  <span>Apple</span>
                </button>

                <button className="social-button facebook-button">
                  <svg
                    className="facebook-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                      fill="#1877F2"
                    />
                  </svg>
                  <span>Facebook</span>
                </button>
              </div>
            </div> */}

            <div className="sign-up-link-container">
              <p className="sign-up-text">
                I don't have an Account{" "}
                <button
                  type="button"
                  className="create-account-link"
                  onClick={() =>
                    navigate(
                      `/signup?role=${selectedRole}&email=${email}&source=SIGNUP`,
                      {
                        state: {
                          from: location.state?.from,
                          role: selectedRole,
                        },
                      }
                    )
                  }
                >
                  Create Account
                </button>
              </p>
            </div>

            <div className="button-container">
              <button
                type="button"
                className="login-back-btn"
                onClick={() => navigate(-1)}
              >
                Back
              </button>

              <button
                type="submit"
                className="mobile-login-button"
                onClick={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Login"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <OtpModal
        visible={otpModalVisible}
        email={email}
        onClose={() => setOtpModalVisible(false)}
        onVerify={handleOtpVerify}
        onResend={() => console.log("Resend OTP")}
        onEditEmail={() => setOtpModalVisible(false)}
      />

      <SetPasswordReminderModal
        isOpen={showSetPasswordReminderModal}
        onContinue={() => {
          setShowSetPasswordReminderModal(false);
          setShowChangePasswordModal(true);
        }}
      />

      <PasswordUpdatedModal
        isOpen={showPasswordUpdatedModal}
        onContinue={handlePasswordUpdatedContinue}
      />

      <ChangePasswordFirstTimeModal
        visible={showChangePasswordModal}
        onClose={() => {
          setShowChangePasswordModal(false);
          setTempAuthData(null);
        }}
        onSubmit={handleChangePasswordSubmit}
        isLoading={isLoading}
        userEmail={tempAuthData?.email || ""}
      />

      <AccessDeniedModal
        isOpen={accessDenied.visible}
        deniedRole={accessDenied.deniedRole}
        availableRoles={accessDenied.availableRoles}
        onClose={() => {
          setAccessDenied({
            visible: false,
            deniedRole: "",
            availableRoles: [],
          });

          navigate("/onboarding", { state: { mode: "login" } });
        }}
      />
    </div>
  );
};

export default LoginScreen;
